import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import villas from "../../../constants/villas.json";
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  Stack,
  Avatar,
  Modal,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
} from "@mui/material";
import {
  Wifi,
  Phone,
  ArrowBackIos,
  ArrowForwardIos,
  Kitchen,
  LocalParking,
  Pool,
  Pets,
  Group,
  Schedule,
  Close,
} from "@mui/icons-material";

import CheckAvailabilityCard from "../../../components/CheckAvailabilityCard";

// Map amenity names to Material UI icons (comprehensive, normalized)
const amenityIconMap = {
  // Staff
  "24-hour building staff": require("@mui/icons-material/SupervisorAccount").default,
  "24/7 building staff": require("@mui/icons-material/SupervisorAccount").default,
  "24hr building staff": require("@mui/icons-material/SupervisorAccount").default,
  // TVs
  "32-inch hdtv": require("@mui/icons-material/Tv").default,
  "32-inch tv with cable": require("@mui/icons-material/Tv").default,
  "43-inch hdtv": require("@mui/icons-material/Tv").default,
  "hdtv": require("@mui/icons-material/Tv").default,
  "hdtv with netflix, amazon prime video, disney+": require("@mui/icons-material/Tv").default,
  "tv": require("@mui/icons-material/Tv").default,
  // Air Conditioning
  "air conditioning": require("@mui/icons-material/AcUnit").default,
  "air conditioning (split)": require("@mui/icons-material/AcUnit").default,
  "window ac unit": require("@mui/icons-material/AcUnit").default,
  "ceiling fan": require("@mui/icons-material/AcUnit").default,
  // Animals
  "assistance animals allowed": require("@mui/icons-material/Pets").default,
  "pets allowed": require("@mui/icons-material/Pets").default,
  // Bath
  "bath": require("@mui/icons-material/Bathtub").default,
  "bidet": require("@mui/icons-material/Water").default,
  "body soap": require("@mui/icons-material/Spa").default,
  "shampoo": require("@mui/icons-material/Spa").default,
  "hair dryer": require("@mui/icons-material/Spa").default,
  "shower gel": require("@mui/icons-material/Shower").default,
  "outdoor shower": require("@mui/icons-material/Shower").default,
  // Beach
  "beach access – beachfront": require("@mui/icons-material/BeachAccess").default,
  "beachfront": require("@mui/icons-material/BeachAccess").default,
  "shared beach access": require("@mui/icons-material/BeachAccess").default,
  // Bed/Bath
  "bed linen": require("@mui/icons-material/Hotel").default,
  "cotton linen": require("@mui/icons-material/Hotel").default,
  "extra pillows & blankets": require("@mui/icons-material/Hotel").default,
  "extra pillows and blankets": require("@mui/icons-material/Hotel").default,
  "towels, bed sheets, soap, and toilet paper": require("@mui/icons-material/Hotel").default,
  "essentials (towels, bed sheets, soap, toilet paper)": require("@mui/icons-material/Hotel").default,
  "essentials (towels, soap, toilet paper, bed sheets)": require("@mui/icons-material/Hotel").default,
  "essentials (towels, soap, toilet paper)": require("@mui/icons-material/Hotel").default,
  // Books
  "books and reading material": require("@mui/icons-material/MenuBook").default,
  // Cleaning
  "cleaning available during stay": require("@mui/icons-material/CleaningServices").default,
  "cleaning products": require("@mui/icons-material/CleaningServices").default,
  "daily housekeeping available": require("@mui/icons-material/CleaningServices").default,
  // Clothes
  "clothes drying rack": require("@mui/icons-material/DryCleaning").default,
  "clothes storage": require("@mui/icons-material/Checkroom").default,
  "clothes storage (wardrobe)": require("@mui/icons-material/Checkroom").default,
  "wardrobe": require("@mui/icons-material/Checkroom").default,
  "hangers": require("@mui/icons-material/Checkroom").default,
  // Cooking
  "cooker": require("@mui/icons-material/Restaurant").default,
  "cooking basics (pots, oil, salt, pepper)": require("@mui/icons-material/Restaurant").default,
  "kitchen": require("@mui/icons-material/Kitchen").default,
  "microwave": require("@mui/icons-material/Microwave").default,
  "mini fridge": require("@mui/icons-material/Kitchen").default,
  "fridge": require("@mui/icons-material/Kitchen").default,
  "freezer": require("@mui/icons-material/Kitchen").default,
  "refrigerator": require("@mui/icons-material/Kitchen").default,
  "lg refrigerator": require("@mui/icons-material/Kitchen").default,
  "morphy richards single oven": require("@mui/icons-material/Restaurant").default,
  "dining table": require("@mui/icons-material/TableBar").default,
  "dishes & cutlery (bowls, plates, cups, etc.)": require("@mui/icons-material/Restaurant").default,
  "dishes and cutlery (bowls, chopsticks, plates, cups)": require("@mui/icons-material/Restaurant").default,
  "dishes and cutlery (bowls, chopsticks, plates, cups, etc.)": require("@mui/icons-material/Restaurant").default,
  "dishes and cutlery (plates, bowls, chopsticks, cups)": require("@mui/icons-material/Restaurant").default,
  "dishes and cutlery (plates, bowls, cups, chopsticks)": require("@mui/icons-material/Restaurant").default,
  "children’s tableware": require("@mui/icons-material/Restaurant").default,
  // Workspace
  "dedicated workspace": require("@mui/icons-material/Work").default,
  // Duplicate for case
  "dedicated workspace": require("@mui/icons-material/Work").default,
  // Dining
  "outdoor dining area": require("@mui/icons-material/OutdoorGrill").default,
  "outdoor furniture": require("@mui/icons-material/Weekend").default,
  // Garden
  "garden view": require("@mui/icons-material/Grass").default,
  "shared garden": require("@mui/icons-material/Grass").default,
  "private back garden (fully fenced)": require("@mui/icons-material/Grass").default,
  // Heating
  "heating": require("@mui/icons-material/Whatshot").default,
  // Luggage
  "luggage drop-off allowed": require("@mui/icons-material/Luggage").default,
  // Long-term
  "long-term stays allowed (28+ days)": require("@mui/icons-material/CalendarMonth").default,
  // Parking
  "free parking on premises": require("@mui/icons-material/LocalParking").default,
  "free parking on premises (1 spot)": require("@mui/icons-material/LocalParking").default,
  // Patio/Balcony
  "private patio or balcony": require("@mui/icons-material/Balcony").default,
  // Pets
  "pets allowed": require("@mui/icons-material/Pets").default,
  // Pool
  "private pool": require("@mui/icons-material/Pool").default,
  "private pool (available all year)": require("@mui/icons-material/Pool").default,
  "shared pool": require("@mui/icons-material/Pool").default,
  "shared pool (next door resort)": require("@mui/icons-material/Pool").default,
  "shared pool access (₹50/person)": require("@mui/icons-material/Pool").default,
  "shared outdoor pool (8:00 am – 5:30 pm)": require("@mui/icons-material/Pool").default,
  // Resort
  "resort access": require("@mui/icons-material/LocationCity").default,
  // Room darkening
  "room-darkening blinds": require("@mui/icons-material/BlindsClosed").default,
  "room-darkening blinds": require("@mui/icons-material/BlindsClosed").default,
  // Self check-in
  "self check-in": require("@mui/icons-material/MeetingRoom").default,
  // Separate entrance
  "private entrance": require("@mui/icons-material/MeetingRoom").default,
  "separate street or building entrance": require("@mui/icons-material/MeetingRoom").default,
  // Sun loungers
  "sun loungers": require("@mui/icons-material/WbSunny").default,
  // Washer
  "washer": require("@mui/icons-material/LocalLaundryService").default,
  "washing machine": require("@mui/icons-material/LocalLaundryService").default,
  // Waste
  "waste compactor": require("@mui/icons-material/Delete").default,
  // Wine
  "wine glasses": require("@mui/icons-material/WineBar").default,
  // Wifi
  "wifi": Wifi,
  // Fallback
  other: require("@mui/icons-material/CircleOutlined").default,
};

const houseRuleIconMap = {
  // Pets
  "no pets": require("@mui/icons-material/DoNotDisturbAlt").default,
  "pets allowed": Pets,
  "pet friendly": Pets,
  "pets welcome": Pets,
  // Parties/Events
  "no parties": require("@mui/icons-material/Block").default,
  "no parties or events": require("@mui/icons-material/Block").default,
  "no events": require("@mui/icons-material/Block").default,
  // Smoking
  "no smoking": require("@mui/icons-material/SmokeFree").default,
  "no smoking indoors": require("@mui/icons-material/SmokeFree").default,
  "smoking allowed": require("@mui/icons-material/SmokingRooms").default,
  "smoking allowed (designated areas)":
    require("@mui/icons-material/SmokingRooms").default,
  "smoking allowed in outdoor areas":
    require("@mui/icons-material/SmokingRooms").default,
  // Check-in/out
  "check-in": Schedule,
  "check-in after": Schedule,
  "check-in at": Schedule,
  "check-out": Schedule,
  "check-out before": Schedule,
  // Guests
  maximum: require("@mui/icons-material/People").default,
  // Quiet hours
  "no loud music": require("@mui/icons-material/VolumeOff").default,
  "quiet hours": require("@mui/icons-material/VolumeOff").default,
  // Luggage
  "luggage drop-off allowed": require("@mui/icons-material/Luggage").default,
  // Cleaning
  "cleaning available during stay":
    require("@mui/icons-material/CleaningServices").default,
  "daily housekeeping available":
    require("@mui/icons-material/CleaningServices").default,
  // Long-term stays
  "long-term stays allowed": require("@mui/icons-material/CalendarMonth")
    .default,
  // Breakfast
  "breakfast available on request": require("@mui/icons-material/FreeBreakfast")
    .default,
  // Fallback
  other: require("@mui/icons-material/HelpOutline").default,
};

const PropertyDetails = () => {
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showAmenitiesModal, setShowAmenitiesModal] = useState(false);

  const { id } = useParams();
  const villa = villas.find((v) => String(v.id) === String(id));
  const amenities = villa?.amenities || [];
  const previewAmenities = amenities.slice(0, 10);
  const houseRules = villa?.houseRules || [];
  const reviews = villa?.reviews || [];
  const host = villa?.host || { name: "", avatar: "" };

  const nextReview = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentReviewIndex((prev) => (prev + 1) % reviews.length);
      setIsTransitioning(false);
    }, 150);
  };

  const prevReview = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentReviewIndex(
        (prev) => (prev - 1 + reviews.length) % reviews.length
      );
      setIsTransitioning(false);
    }, 150);
  };

  const goToReview = (index) => {
    if (isTransitioning || index === currentReviewIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentReviewIndex(index);
      setIsTransitioning(false);
    }, 150);
  };

  // Autoplay functionality
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isTransitioning) {
        nextReview();
      }
    }, 4000); // Change review every 4 seconds

    return () => clearInterval(interval);
  }, [isTransitioning]);

  return (
    <Box sx={{ pb: 8 }}>
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Left Content - Scrollable */}
          <Grid item size={{ xs: 12, lg: 8 }}>
            <Box sx={{ pr: { lg: 4 } }}>
              {/* Description Section */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "Monument-Private",
                    fontWeight: 700,
                    mb: 1,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                  }}
                >
                  Description
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.secondary",
                    lineHeight: 1.7,
                    fontSize: "1rem",
                  }}
                >
                  {villa.description}
                </Typography>
              </Box>

              {/* Amenities Section */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "Monument-Private",
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                  }}
                >
                  Amenities
                </Typography>
                <Grid container spacing={2}>
                  {previewAmenities.map((amenity, index) => {
                    // Normalize amenity name for mapping
                    const normalized = String(amenity).trim().toLowerCase();
                    const IconComponent =
                      amenityIconMap[normalized] ||
                      amenityIconMap["other"] ||
                      Wifi;
                    return (
                      <Grid item xs={6} md={6} key={index}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            py: 1,
                          }}
                        >
                          <IconComponent
                            sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                          />
                          <Typography
                            variant="body1"
                            sx={{ color: "text.secondary" }}
                          >
                            {amenity}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
                <Button
                  variant="outlined"
                  onClick={() => setShowAmenitiesModal(true)}
                  sx={{
                    mt: 3,
                    color: "text.primary",
                    borderColor: "rgba(0, 0, 0, 0.2)",
                    textTransform: "none",
                    fontWeight: 500,
                    "&:hover": {
                      borderColor: "rgba(0, 0, 0, 0.4)",
                    },
                  }}
                >
                  See all amenities
                </Button>
              </Box>

              {/* House Rules Section */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "Monument-Private",
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                  }}
                >
                  House rules
                </Typography>
                <Stack spacing={2}>
                  {houseRules.map((rule, index) => {
                    // If rule is an object with text, use rule.text, else use rule as string
                    const ruleText =
                      typeof rule === "string" ? rule : rule.text;
                    const normalized = String(ruleText).trim().toLowerCase();
                    let IconComponent = houseRuleIconMap["other"];
                    // 1. Try to match the start of the rule to a key in the map
                    for (const key in houseRuleIconMap) {
                      if (key !== "other" && normalized.startsWith(key)) {
                        IconComponent = houseRuleIconMap[key];
                        break;
                      }
                    }
                    // 2. Special handling for guest maximum rules (e.g., "8 guests maximum")
                    if (
                      IconComponent === houseRuleIconMap["other"] &&
                      (/guests maximum$/.test(normalized) ||
                        /maximum guests$/.test(normalized) ||
                        /guests: maximum/.test(normalized) ||
                        /maximum of \d+ guests/.test(normalized) ||
                        /\d+ guests maximum/.test(normalized) ||
                        /maximum \d+ guests/.test(normalized) ||
                        /guest limit/.test(normalized) ||
                        /guests only/.test(normalized))
                    ) {
                      IconComponent =
                        require("@mui/icons-material/People").default;
                    }
                    return (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          py: 1,
                        }}
                      >
                        <IconComponent
                          sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                        />
                        <Typography
                          variant="body1"
                          sx={{ color: "text.secondary" }}
                        >
                          {ruleText}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>

              {/* Hosted by Section */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "Monument-Private",
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                  }}
                >
                  Hosted by
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: 2,
                    justifyContent: "space-between",
                    p: 3,
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    borderRadius: 3,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      src="/villa-01.png"
                      sx={{ width: 60, height: 60 }}
                    />
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: "Monument-Private",
                          fontWeight: 600,
                          mb: 0.5,
                        }}
                      >
                        Aspire Ventures
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<Phone />}
                    sx={{
                      borderColor: "rgba(0, 0, 0, 0.2)",
                      color: "text.primary",
                      textTransform: "none",
                      fontWeight: 500,
                      "&:hover": {
                        borderColor: "rgba(0, 0, 0, 0.4)",
                      },
                    }}
                    component="a"
                    href={`tel:+919673244873`}
                  >
                    Call now
                  </Button>
                </Box>
              </Box>

              {/* Location Section */}
              <Box sx={{ mb: 6 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontFamily: "Monument-Private",
                    fontWeight: 700,
                    mb: 3,
                    fontSize: { xs: "1.5rem", md: "2rem" },
                  }}
                >
                  Location
                </Typography>
                <Box
                  sx={{
                    height: "400px",
                    borderRadius: 3,
                    overflow: "hidden",
                    backgroundColor: "#f0f0f0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {villa?.mapEmbedUrl ? (
                    <iframe
                      src={villa.mapEmbedUrl}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Property Location"
                    />
                  ) : (
                    <Typography
                      variant="body1"
                      sx={{ color: "text.secondary" }}
                    >
                      Map not available
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Reviews Section */}
              <Box sx={{ mb: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 3,
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: "Monument-Private",
                      fontWeight: 700,
                      fontSize: { xs: "1.5rem", md: "2rem" },
                    }}
                  >
                    Reviews
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      onClick={prevReview}
                      sx={{
                        minWidth: "40px",
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(0, 0, 0, 0.05)",
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.1)",
                        },
                      }}
                      disabled={reviews.length === 0}
                    >
                      <ArrowBackIos sx={{ fontSize: "1rem" }} />
                    </Button>
                    <Button
                      onClick={nextReview}
                      sx={{
                        minWidth: "40px",
                        width: "40px",
                        height: "40px",
                        borderRadius: "50%",
                        backgroundColor: "rgba(0, 0, 0, 0.05)",
                        color: "text.primary",
                        "&:hover": {
                          backgroundColor: "rgba(0, 0, 0, 0.1)",
                        },
                      }}
                      disabled={reviews.length === 0}
                    >
                      <ArrowForwardIos sx={{ fontSize: "1rem" }} />
                    </Button>
                  </Box>
                </Box>

                {reviews.length > 0 ? (
                  <Card
                    sx={{
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                      borderRadius: 3,
                      p: 3,
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      opacity: isTransitioning ? 0 : 1,
                      transform: isTransitioning
                        ? "translateY(10px)"
                        : "translateY(0)",
                      transition: "all 0.3s ease-in-out",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                    >
                      <Avatar
                        src={reviews[currentReviewIndex].avatar}
                        sx={{
                          width: 50,
                          height: 50,
                          transition: "all 0.3s ease-in-out",
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontFamily: "Monument-Private",
                            fontWeight: 600,
                            mb: 0.5,
                            transition: "all 0.3s ease-in-out",
                          }}
                        >
                          {reviews[currentReviewIndex].name}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mb: 1,
                          }}
                        >
                          {[...Array(5)].map((_, index) => (
                            <Box
                              key={index}
                              component="img"
                              src="/star.svg"
                              sx={{
                                width: "16px",
                                height: "16px",
                                filter:
                                  index < reviews[currentReviewIndex].rating
                                    ? "none"
                                    : "grayscale(100%) brightness(200%)",
                                transition: "all 0.3s ease-in-out",
                              }}
                            />
                          ))}
                        </Box>
                        <Typography
                          variant="body1"
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.6,
                            transition: "all 0.3s ease-in-out",
                          }}
                        >
                          {reviews[currentReviewIndex].text}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                ) : (
                  <Card
                    sx={{
                      border: "1px solid rgba(0, 0, 0, 0.1)",
                      borderRadius: 3,
                      p: 3,
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ color: "text.secondary" }}
                    >
                      No reviews yet for this property.
                    </Typography>
                  </Card>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Right Sidebar - Sticky Booking Box */}
          <Grid item size={{ xs: 12, lg: 4 }}>
            <Box
              sx={{
                position: { lg: "sticky" },
                top: { lg: "100px" },
                height: "fit-content",
              }}
            >
              {/* Pass the villa name as stayName prop to CheckAvailabilityCard */}
              <CheckAvailabilityCard stayName={villa?.name || ""} />
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Amenities Modal */}
      <Modal
        open={showAmenitiesModal}
        onClose={() => setShowAmenitiesModal(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card
          sx={{
            width: { xs: "90%", sm: "500px" },
            maxHeight: "80vh",
            overflow: "hidden",
            outline: "none",
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 3,
              borderBottom: "1px solid rgba(0, 0, 0, 0.1)",
              flexShrink: 0,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontFamily: "Monument-Private",
                fontWeight: 700,
              }}
            >
              All Amenities
            </Typography>
            <IconButton
              onClick={() => setShowAmenitiesModal(false)}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Modal Content - Scrollable */}
          <Box
            sx={{
              overflow: "auto",
              flex: 1,
            }}
          >
            <List sx={{ p: 0 }}>
              {amenities.map((amenity, index) => {
                const normalized = String(amenity).trim().toLowerCase();
                const IconComponent =
                  amenityIconMap[normalized] || amenityIconMap["other"] || Wifi;
                return (
                  <ListItem
                    key={index}
                    sx={{
                      py: 2,
                      px: 3,
                      borderBottom:
                        index < amenities.length - 1
                          ? "1px solid rgba(0, 0, 0, 0.06)"
                          : "none",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: "40px" }}>
                      <IconComponent
                        sx={{ color: "text.secondary", fontSize: "1.2rem" }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={amenity}
                      primaryTypographyProps={{
                        variant: "body1",
                        sx: { color: "text.primary", fontWeight: 500 },
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Card>
      </Modal>
    </Box>
  );
};

export default PropertyDetails;
